/**
 * PlatformClinicsPanel — Founder-only clinic management.
 *
 * Rendered below the existing analytics dashboard on /platform. Deliberately
 * minimal: search, list, and 6 actions (edit / extend trial / change plan /
 * suspend / activate / delete). No redesign of the parent page.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Search, MoreVertical, Loader2, Pencil, Clock, CreditCard,
  PauseCircle, PlayCircle, Trash2, AlertTriangle, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { useClinicConfig } from "@/lib/clinicConfig";

const PLAN_OPTIONS = [
  { id: "trial",          label: "Free Trial" },
  { id: "solo_monthly",   label: "Solo · Monthly" },
  { id: "solo_yearly",    label: "Solo · Yearly" },
  { id: "clinic_monthly", label: "Clinic · Monthly" },
  { id: "clinic_yearly",  label: "Clinic · Yearly" },
];

const STATUS_TONE = {
  active:    "bg-[#E8F0EB] text-[#2D6A4F]",
  trialing:  "bg-[#FDF3ED] text-[#8A3F1E]",
  past_due:  "bg-[#FCECEC] text-[#B94A48]",
  expired:   "bg-[#EDEDED] text-[#6B7280]",
  cancelled: "bg-[#EDEDED] text-[#6B7280]",
  suspended: "bg-[#FCECEC] text-[#B94A48]",
};

function fmtMB(bytes) {
  if (!bytes) return "0 MB";
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function PlatformClinicsPanel() {
  const [clinics, setClinics] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [modal, setModal] = useState(null); // {type, clinic}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/platform/clinics${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`,
      );
      setClinics(data.clinics || []);
    } catch (e) {
      toast.error("Could not load clinics");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const closeMenu = () => setOpenMenuId(null);
  const openModal = (type, clinic) => { setModal({ type, clinic }); closeMenu(); };
  const onDone = () => { setModal(null); load(); };

  return (
    <section className="card-widget mt-10" data-testid="platform-clinics-panel">
      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        <div>
          <div className="overline text-ink-soft">Clinics</div>
          <div className="font-display text-2xl mt-1">{clinics.length} on the platform</div>
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-ink-soft absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="clinic-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, slug, email…"
            className="w-full pl-9 pr-3 py-2 rounded-full bg-linen border border-[#E7E1D3] text-sm focus:outline-none focus:border-pine focus:ring-2 focus:ring-pine/10"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-ink-soft"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
      ) : clinics.length === 0 ? (
        <div className="py-8 text-center text-sm text-ink-soft">No clinics match your search.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-soft overline">
                <th className="py-2 pr-3">Clinic</th>
                <th className="pr-3">Owner</th>
                <th className="pr-3">Plan</th>
                <th className="pr-3">Status</th>
                <th className="pr-3">Trial ends</th>
                <th className="pr-3 text-right">Users</th>
                <th className="pr-3 text-right">Patients</th>
                <th className="pr-3 text-right">Storage</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {clinics.map((c) => (
                <tr key={c.id} className="border-t border-[#E8EAE9]" data-testid={`clinic-row-${c.id}`}>
                  <td className="py-3 pr-3">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-[11px] text-ink-soft font-mono">/{c.slug}</div>
                  </td>
                  <td className="pr-3 text-ink-mid">
                    <div className="truncate max-w-[180px]">{c.owner?.email || "—"}</div>
                    {c.owner?.last_login_at && (
                      <div className="text-[10px] text-ink-soft">
                        last · {new Date(c.owner.last_login_at).toLocaleDateString("en-IN")}
                      </div>
                    )}
                  </td>
                  <td className="pr-3 text-ink-mid">{c.plan || "—"}</td>
                  <td className="pr-3">
                    <span className={`text-[10px] px-2 py-1 rounded-full uppercase tracking-widest ${STATUS_TONE[c.subscription_status] || "bg-[#EDEDED] text-[#6B7280]"}`}>
                      {c.subscription_status || "—"}
                    </span>
                  </td>
                  <td className="pr-3 text-ink-mid text-xs">
                    {c.trial_ends_at ? new Date(c.trial_ends_at).toLocaleDateString("en-IN") : "—"}
                  </td>
                  <td className="pr-3 text-right text-ink-mid">{c.users_count ?? 0}</td>
                  <td className="pr-3 text-right text-ink-mid">{c.patients_count ?? 0}</td>
                  <td className="pr-3 text-right text-ink-mid">{fmtMB(c.usage_storage_bytes)}</td>
                  <td className="relative">
                    <button
                      data-testid={`clinic-menu-${c.id}`}
                      onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === c.id ? null : c.id); }}
                      className="p-1.5 rounded-lg hover:bg-linen">
                      <MoreVertical className="w-4 h-4 text-ink-mid" />
                    </button>
                    {openMenuId === c.id && (
                      <ActionMenu
                        clinic={c}
                        onClose={closeMenu}
                        onOpenModal={openModal}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ActionModal type={modal.type} clinic={modal.clinic}
                     onClose={() => setModal(null)} onDone={onDone} />
      )}
    </section>
  );
}


/* ------------------------------------------------------- Dropdown menu ---- */
function ActionMenu({ clinic, onClose, onOpenModal }) {
  const isSuspended = clinic.subscription_status === "suspended";
  useEffect(() => {
    const h = () => onClose();
    window.addEventListener("click", h);
    return () => window.removeEventListener("click", h);
  }, [onClose]);

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="absolute right-0 top-9 z-30 w-56 rounded-xl bg-white border border-[#E8EAE9] shadow-lg py-1.5"
      data-testid={`clinic-actions-${clinic.id}`}>
      <MenuItem icon={Pencil}    label="Edit clinic"       onClick={() => onOpenModal("edit", clinic)}    testid={`action-edit-${clinic.id}`} />
      <MenuItem icon={Clock}     label="Extend trial"      onClick={() => onOpenModal("extend", clinic)}  testid={`action-extend-${clinic.id}`} />
      <MenuItem icon={CreditCard} label="Change plan"       onClick={() => onOpenModal("plan", clinic)}    testid={`action-plan-${clinic.id}`} />
      {isSuspended ? (
        <MenuItem icon={PlayCircle} label="Activate clinic"  onClick={() => onOpenModal("activate", clinic)} testid={`action-activate-${clinic.id}`} />
      ) : (
        <MenuItem icon={PauseCircle} label="Suspend clinic"  onClick={() => onOpenModal("suspend", clinic)}  testid={`action-suspend-${clinic.id}`} />
      )}
      <div className="border-t border-[#E8EAE9] my-1" />
      <MenuItem icon={Trash2}    label="Delete clinic"      onClick={() => onOpenModal("delete", clinic)} danger testid={`action-delete-${clinic.id}`} />
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-linen ${
        danger ? "text-[#B94A48]" : "text-ink"
      }`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}


/* ------------------------------------------------------- Modal shell ------ */
function ActionModal({ type, clinic, onClose, onDone }) {
  const titles = {
    edit:     "Edit clinic",
    extend:   "Extend trial",
    plan:     "Change plan",
    suspend:  "Suspend clinic",
    activate: "Activate clinic",
    delete:   "Delete clinic",
  };

  return (
    <div className="fixed inset-0 z-40 bg-ink/40 flex items-center justify-center p-4"
         onClick={onClose}
         data-testid={`modal-${type}`}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <header className="flex items-center justify-between px-5 py-4 border-b border-[#E8EAE9]">
          <div>
            <div className="font-display text-xl">{titles[type]}</div>
            <div className="text-xs text-ink-soft">{clinic.name} · {clinic.slug}</div>
          </div>
          <button data-testid="modal-close" onClick={onClose}
                  className="p-1.5 rounded-lg hover:bg-linen">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="p-5">
          {type === "edit"     && <EditForm clinic={clinic} onDone={onDone} />}
          {type === "extend"   && <ExtendForm clinic={clinic} onDone={onDone} />}
          {type === "plan"     && <PlanForm clinic={clinic} onDone={onDone} />}
          {type === "suspend"  && <SimpleConfirm clinic={clinic} label="Suspend" onDone={onDone}
                                    action={() => api.post(`/platform/clinics/${clinic.id}/suspend`)}
                                    warning="The clinic team will lose access until you reactivate." />}
          {type === "activate" && <SimpleConfirm clinic={clinic} label="Activate" onDone={onDone}
                                    action={() => api.post(`/platform/clinics/${clinic.id}/activate`)}
                                    warning={null} />}
          {type === "delete"   && <DeleteForm clinic={clinic} onDone={onDone} />}
        </div>
      </div>
    </div>
  );
}


function EditForm({ clinic, onDone }) {
  const [form, setForm] = useState({
    name:    clinic.name || "",
    email:   clinic.email || "",
    phone:   clinic.phone || "",
    address: clinic.address || "",
    state:   clinic.state || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/platform/clinics/${clinic.id}`, form);
      toast.success("Clinic updated");
      onDone();
    } catch (e) {
      toast.error(apiError(e, "Update failed"));
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/10";
  const lbl = "block text-[10px] uppercase tracking-widest text-ink-soft mb-1";
  return (
    <div className="space-y-3">
      <div><label className={lbl}>Name</label><input data-testid="edit-name" className={inp} value={form.name} onChange={set("name")} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={lbl}>Email</label><input data-testid="edit-email" className={inp} value={form.email} onChange={set("email")} /></div>
        <div><label className={lbl}>Phone</label><input data-testid="edit-phone" className={inp} value={form.phone} onChange={set("phone")} /></div>
      </div>
      <div><label className={lbl}>Address</label><input data-testid="edit-address" className={inp} value={form.address} onChange={set("address")} /></div>
      <div><label className={lbl}>State</label><input data-testid="edit-state" className={inp} value={form.state} onChange={set("state")} /></div>
      <div className="flex justify-end pt-2">
        <button data-testid="edit-save" onClick={save} disabled={saving}
                className="px-4 py-2 rounded-full bg-pine text-linen text-sm hover:bg-pine/90 disabled:opacity-50">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> : null}
          Save changes
        </button>
      </div>
    </div>
  );
}


function ExtendForm({ clinic, onDone }) {
  const [days, setDays] = useState(14);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/platform/clinics/${clinic.id}/extend-trial`, { extra_days: Number(days) });
      toast.success(`Trial extended until ${new Date(data.trial_ends_at).toLocaleDateString("en-IN")}`);
      onDone();
    } catch (e) {
      toast.error(apiError(e, "Could not extend"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-3">
      <label className="block text-sm">
        <span className="block text-[10px] uppercase tracking-widest text-ink-soft mb-1">Additional days</span>
        <input data-testid="extend-days" type="number" min="1" max="365"
               value={days} onChange={(e) => setDays(e.target.value)}
               className="w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/10" />
      </label>
      <div className="text-xs text-ink-mid">Current end: {clinic.trial_ends_at ? new Date(clinic.trial_ends_at).toLocaleDateString("en-IN") : "—"}</div>
      <div className="flex justify-end pt-2">
        <button data-testid="extend-submit" onClick={submit} disabled={saving}
                className="px-4 py-2 rounded-full bg-pine text-linen text-sm hover:bg-pine/90 disabled:opacity-50">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />}
          Extend by {days} day{Number(days) === 1 ? "" : "s"}
        </button>
      </div>
    </div>
  );
}


function PlanForm({ clinic, onDone }) {
  const [plan, setPlan] = useState(clinic.plan || "trial");
  const [status, setStatus] = useState(clinic.subscription_status === "trialing" ? "trialing" : "active");
  const [saving, setSaving] = useState(false);
  const cfg = useClinicConfig();
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/platform/clinics/${clinic.id}/change-plan`, { plan, status });
      toast.success("Plan updated");
      // If the founder is ALSO signed in as this clinic in another tab,
      // broadcast so their Calendar / Doctors / Subscription refresh without
      // logout. Non-founder admins of the target clinic pick this up on
      // their own 15s poll.
      cfg.invalidateAllTabs?.();
      onDone();
    } catch (e) {
      toast.error(apiError(e, "Could not change plan"));
    } finally {
      setSaving(false);
    }
  };
  const sel = "w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm bg-white focus:border-pine focus:outline-none";
  return (
    <div className="space-y-3">
      <label className="block text-sm">
        <span className="block text-[10px] uppercase tracking-widest text-ink-soft mb-1">Plan</span>
        <select data-testid="plan-select" value={plan} onChange={(e) => setPlan(e.target.value)} className={sel}>
          {PLAN_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </label>
      <label className="block text-sm">
        <span className="block text-[10px] uppercase tracking-widest text-ink-soft mb-1">Status</span>
        <select data-testid="plan-status" value={status} onChange={(e) => setStatus(e.target.value)} className={sel}>
          {["active", "trialing", "past_due", "expired", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>
      <div className="flex justify-end pt-2">
        <button data-testid="plan-submit" onClick={submit} disabled={saving}
                className="px-4 py-2 rounded-full bg-pine text-linen text-sm hover:bg-pine/90 disabled:opacity-50">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />}
          Save plan
        </button>
      </div>
    </div>
  );
}


function SimpleConfirm({ clinic, label, warning, action, onDone }) {
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await action();
      toast.success(`${label} succeeded`);
      onDone();
    } catch (e) {
      toast.error(apiError(e, `${label} failed`));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-4">
      {warning && (
        <div className="rounded-xl bg-[#FDF3ED] text-[#8A3F1E] px-4 py-3 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {warning}
        </div>
      )}
      <p className="text-sm text-ink-mid">
        Confirm you want to <strong>{label.toLowerCase()}</strong> <strong>{clinic.name}</strong>?
      </p>
      <div className="flex justify-end">
        <button data-testid="confirm-submit" onClick={submit} disabled={saving}
                className="px-4 py-2 rounded-full bg-pine text-linen text-sm hover:bg-pine/90 disabled:opacity-50">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />}
          {label}
        </button>
      </div>
    </div>
  );
}


function DeleteForm({ clinic, onDone }) {
  const [typed, setTyped] = useState("");
  const [saving, setSaving] = useState(false);
  const canDelete = typed.trim() === clinic.slug;

  const submit = async () => {
    if (!canDelete) return;
    setSaving(true);
    try {
      await api.delete(`/platform/clinics/${clinic.id}?confirm_slug=${encodeURIComponent(clinic.slug)}`);
      toast.success("Clinic deleted");
      onDone();
    } catch (e) {
      toast.error(apiError(e, "Delete failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-[#FCECEC] text-[#B94A48] px-4 py-3 text-sm flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          This <strong>permanently deletes</strong> the clinic and every patient,
          appointment, invoice, prescription, and file it owns. This cannot be undone.
        </div>
      </div>
      <label className="block text-sm">
        <span className="block text-[10px] uppercase tracking-widest text-ink-soft mb-1">
          Type the slug <code className="text-[#B94A48] font-mono">{clinic.slug}</code> to confirm
        </span>
        <input
          data-testid="delete-confirm-input"
          value={typed} onChange={(e) => setTyped(e.target.value)}
          className="w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm focus:border-[#B94A48] focus:outline-none focus:ring-2 focus:ring-[#B94A48]/10 font-mono"
          autoFocus
        />
      </label>
      <div className="flex justify-end">
        <button data-testid="delete-submit" onClick={submit} disabled={!canDelete || saving}
                className="px-4 py-2 rounded-full bg-[#B94A48] text-white text-sm hover:bg-[#8f3735] disabled:opacity-40 disabled:cursor-not-allowed">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />}
          Delete forever
        </button>
      </div>
    </div>
  );
}
